import { type CardDisputeDocumentEvidenceInput } from '@column/api';

export const CARD_DISPUTE_EVIDENCE_ACCEPT = '.pdf,.jpg,.jpeg,.tif,.tiff,application/pdf,image/jpeg,image/tiff';

export const PDF_MAX_BYTES = 2 * 1024 * 1024;
export const IMAGE_MAX_BYTES = 10 * 1024 * 1024;

const FILE_RULES: Record<string, { mime: string; label: string; maxBytes: number }> = {
  pdf: { mime: 'application/pdf', label: 'PDF', maxBytes: PDF_MAX_BYTES },
  jpg: { mime: 'image/jpeg', label: 'JPEG', maxBytes: IMAGE_MAX_BYTES },
  jpeg: { mime: 'image/jpeg', label: 'JPEG', maxBytes: IMAGE_MAX_BYTES },
  tif: { mime: 'image/tiff', label: 'TIFF', maxBytes: IMAGE_MAX_BYTES },
  tiff: { mime: 'image/tiff', label: 'TIFF', maxBytes: IMAGE_MAX_BYTES },
};

export interface CardDisputeEvidenceFile {
  name: string;
  size: number;
  type: string;
}

export const validateCardDisputeEvidenceFile = (file: CardDisputeEvidenceFile): string | undefined => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const rule = extension ? FILE_RULES[extension] : undefined;

  if (!rule) {
    return `${file.name}: Visa accepts PDF, JPEG, or TIFF evidence. PNG is not supported.`;
  }

  if (file.type.toLowerCase() !== rule.mime) {
    const reportedType = file.type || 'no media type';
    return `${file.name}: the .${extension} extension does not match the reported media type (${reportedType}).`;
  }

  if (file.size > rule.maxBytes) {
    const limit = rule.maxBytes === PDF_MAX_BYTES ? '2 MiB' : '10 MiB';
    return `${file.name}: ${rule.label} evidence must be ${limit} or smaller.`;
  }

  return undefined;
};

export const upsertEvidenceValue = (
  value: CardDisputeDocumentEvidenceInput[],
  evidence: CardDisputeDocumentEvidenceInput
): CardDisputeDocumentEvidenceInput[] => [
  ...value.filter((entry) => entry.document_id !== evidence.document_id),
  evidence,
];

export const removeEvidenceValue = (
  value: CardDisputeDocumentEvidenceInput[],
  documentId: string
): CardDisputeDocumentEvidenceInput[] => value.filter((entry) => entry.document_id !== documentId);

export const getEvidenceErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'object' && error && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return fallback;
};
